import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { useAccount, useBalance, useDisconnect } from 'wagmi'
import { useMediaQuery } from '@/hooks/use-media-query'
import { ellipseAddress, formatBalance } from '@/lib/display'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { PowerIcon } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { HyperevmIcon } from '@zapify-dev/crypto-icons'
import { useTranslation } from '@pancakeswap/localization'
import { useAllSortedRecentTransactions } from '@/state/transactions/hooks'
import isEmpty from 'lodash/isEmpty'
import groupBy from 'lodash/groupBy'
import { Flex, Text } from '@pancakeswap/uikit'
import { TransactionDetails } from '@/state/transactions/reducer'
import Transaction from '@/components/App/Transactions/Transaction'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ThemeSelector } from '@/components/theme-selector'

function renderTransactions(transactions: TransactionDetails[], chainId: number) {
  return (
    <Flex flexDirection="column">
      {transactions.map((tx) => {
        return <Transaction key={tx.hash + tx.addedTime} tx={tx} chainId={chainId} />
      })}
    </Flex>
  )
}

export const ProfileDrawer = () => {
  const { t } = useTranslation()
  const { address } = useAccount()
  const { disconnect } = useDisconnect()
  const { data } = useBalance({ address })
  const isDesktop = useMediaQuery('(min-width: 768px)')

  const sortedRecentTransactions = useAllSortedRecentTransactions()
  const hasTransactions = !isEmpty(sortedRecentTransactions)

  return (
    <Drawer direction={isDesktop ? 'right' : 'bottom'}>
      <DrawerTrigger asChild className={'-mx-4'}>
        <Button variant={'ghost'} className={'flex items-center gap-2 rounded-full py-1'}>
          <Avatar>
            <AvatarImage src={`https://cdn.stamp.fyi/avatar/${address}`} alt={'avatar'} width={48} height={48} />
            <AvatarFallback>Z</AvatarFallback>
          </Avatar>
          {ellipseAddress(address, 5)}
        </Button>
      </DrawerTrigger>
      <DrawerContent className={'p-6'}>
        <div className="flex items-center gap-2 text-secondary-foreground mb-6">
          <Avatar>
            <AvatarImage src={`https://cdn.stamp.fyi/avatar/${address}`} alt={'avatar'} width={48} height={48} />
            <AvatarFallback>Zap</AvatarFallback>
          </Avatar>
          {ellipseAddress(address)}
          <div className="ml-auto">
            <Button variant={'icon'} onClick={() => disconnect()}>
              <PowerIcon />
            </Button>
          </div>
        </div>

        <div className="flex gap-x-2 items-center mb-14">
          <HyperevmIcon className={'size-8'} />
          <Typography variant={'h3'}>{formatBalance(data?.value)} HYPE</Typography>
        </div>

        <Tabs defaultValue={'settings'}>
          <TabsList className={'bg-transparent gap-6 px-0'}>
            <TabsTrigger value={'settings'} className={'shadow-none! px-0 text-base'}>
              {t('Settings')}
            </TabsTrigger>
            <TabsTrigger value={'transactions'} className={'shadow-none! px-0 text-base'}>
              {t('Transactions')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value={'settings'}>
            <div className="flex items-center justify-between">
              <div>{t('Theme')}</div>
              <ThemeSelector />
            </div>
          </TabsContent>
          <TabsContent value={'transactions'}>
            {Object.entries(sortedRecentTransactions).map(([chainId, transactions]) => {
              const chainIdNumber = Number(chainId)
              const groupedTransactions = groupBy(Object.values(transactions), (trxDetails) =>
                Boolean(trxDetails.receipt),
              )

              const confirmed = groupedTransactions.true ?? []
              const pending = groupedTransactions.false ?? []

              return (
                <div key={`transactions#${chainIdNumber}`}>
                  {renderTransactions(pending, chainIdNumber)}
                  {renderTransactions(confirmed, chainIdNumber)}
                </div>
              )
            })}
          </TabsContent>
        </Tabs>
      </DrawerContent>
    </Drawer>
  )
}
